import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { stores as storesApi } from '../lib/api';
import ProductCard from '../components/ProductCard';
import SimilarItems from '../components/SimilarItems';
import WhatsAppShare from '../components/WhatsAppShare';
import ReviewSection from '../components/ReviewSection';
import ReputationPanel from '../components/ReputationPanel';
import CommunityTrustPanel, { ReportStoreDialog } from '../components/CommunityTrustPanel';
import VoiceOrderButton from '../components/VoiceOrderButton';
import StoreScoreBadge from '../components/StoreScoreBadge';
import { useCart } from '../lib/cart';
import { MapPin, Star, Truck, ShoppingBag, ArrowRight, CheckCircle2, Clock, Languages, Calendar, Undo2, ShieldCheck, Phone, Accessibility, BadgeCheck, ScrollText } from 'lucide-react';

export default function StoreDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const cart = useCart();

  useEffect(() => { storesApi.get(id).then(setData).catch(() => {}); }, [id]);
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-16 text-ink/60">Loading store…</div>;
  const { store, products } = data;
  const onStoreUpdated = (patched) => setData({ ...data, store: { ...data.store, ...patched } });

  return (
    <div>
      <div className="relative h-56 md:h-72 overflow-hidden">
        <img src={store.image_url} alt={store.name} className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"/>
        <div className="absolute inset-x-0 bottom-0 max-w-7xl mx-auto px-4 md:px-6 pb-6 text-white">
          <span className="pill !bg-white/90 !text-ink">{store.category}</span>
          <h1 data-testid="store-name" className="font-heading font-black text-3xl md:text-5xl mt-2">{store.name}</h1>
          {store.trust?.verified && (
            <span data-testid="store-verified-pill" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#1B5E20] text-white px-3 py-1 text-xs font-bold shadow-sm">
              <BadgeCheck className="w-3.5 h-3.5"/>Verified Dukandaar · Trust score {store.trust.score}/100
            </span>
          )}
          {!store.trust?.verified && store.trust?.score >= 50 && (
            <span data-testid="store-trust-pill" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/90 text-ink px-3 py-1 text-xs font-bold shadow-sm">
              <ShieldCheck className="w-3.5 h-3.5 text-[#1B5E20]"/>Trust score {store.trust.score}/100 · {store.trust.tier}
            </span>
          )}
          <StoreScoreBadge storeId={store.id}/>
          <div className="mt-1 flex flex-wrap items-center gap-4 text-sm">
            <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4"/>{store.village} · {store.address}</span>
            <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 fill-[#F1C40F] text-[#F1C40F]"/>{store.rating?.toFixed?.(1)}</span>
            <span className="inline-flex items-center gap-1"><Truck className="w-4 h-4"/>₹{store.delivery_fee} delivery · Min ₹{store.min_order}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 grid lg:grid-cols-[1fr_320px] gap-8">
        <div>
          {/* Store trust & info card */}
          <section data-testid="store-info" className="card-soft p-5 mb-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-heading font-bold text-lg">About this store</div>
              <WhatsAppShare
                testId="share-store-whatsapp"
                label="Share on WhatsApp"
                text={`🛒 *BharatCart* — India's Marketplace — Brands, Makers & Local Shops\n\n*${store.name}* (${store.village})\n${store.description}\n\nOrder directly:\n${window.location.href}`}
              />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {store.established_year && (
                <Info Icon={Calendar} label="Established" value={`${store.established_year} · ${new Date().getFullYear() - store.established_year} yrs`}/>
              )}
              {(store.languages || []).length > 0 && (
                <Info Icon={Languages} label="Languages" value={store.languages.join(', ')}/>
              )}
              {store.hours && <Info Icon={Clock} label="Open" value={store.hours}/>}
              {store.verified && <Info Icon={CheckCircle2} label="Verified" value="Trusted by BharatCart" tone="success"/>}
              {typeof store.trust_score === 'number' && (
                <Info Icon={ShieldCheck} label="Owner trust" value={`${store.trust_score}%`}/>
              )}
              {store.phone && <Info Icon={Phone} label="Phone" value={store.phone}/>}
              {store.accessibility_notes && <Info Icon={Accessibility} label="Accessibility" value={store.accessibility_notes}/>}
              {store.return_policy && <Info Icon={Undo2} label="Returns" value={store.return_policy}/>}
            </div>
          </section>

          {(store.photos && store.photos.length > 0) && (
            <section data-testid="shop-gallery" className="card-soft p-5 mb-6">
              <div className="font-heading font-bold text-lg mb-3">Shop photos</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {store.photos.map((p, i) => (
                  <a key={p} href={p} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg overflow-hidden border border-border bg-muted">
                    <img src={p} alt={`${store.name} photo ${i+1}`} className="w-full h-full object-cover hover:scale-105 transition"/>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Community Trust: vouches + reports + trust breakdown */}
          <CommunityTrustPanel store={store} onStoreUpdated={onStoreUpdated}/>

          {/* Voice order — accessibility-first ordering (large, clear) */}
          <section data-testid="voice-order-card" className="card-soft p-5 mb-6 border-2 border-[#0D3F12]/20">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#0D3F12]">Order by voice</div>
                <div className="font-heading font-black text-lg mt-0.5">Can&apos;t type? Just talk to the shop.</div>
                <p className="text-xs text-ink/70 mt-1">
                  Tap the button, speak your order (max 60 s), and we&apos;ll send it straight to {store.name}&apos;s WhatsApp with a link to your recording.
                </p>
              </div>
              <div className="w-full md:w-auto md:min-w-[220px]">
                <VoiceOrderButton store={store} size="large"/>
              </div>
            </div>
          </section>

          {/* Wall-license photo — public proof (Community Trust requirement) */}
          {store.license_photo_url && (
            <section data-testid="store-license-photo" className="card-soft p-5 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <ScrollText className="w-4 h-4 text-[#1B5E20]"/>
                <div className="font-heading font-bold text-lg">Shop licence (verified on-wall)</div>
                {store.license_number && (
                  <span className="ml-2 text-[11px] font-mono uppercase tracking-wider text-ink/60">
                    #{store.license_number}
                  </span>
                )}
                {store.license_authority && (
                  <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-[#1B5E20]">
                    {store.license_authority}
                  </span>
                )}
              </div>
              <a href={store.license_photo_url} target="_blank" rel="noreferrer" className="block">
                <img
                  src={store.license_photo_url}
                  alt={`${store.name} licence`}
                  className="max-h-64 rounded-lg border border-border object-contain bg-muted mx-auto"
                />
              </a>
              <div className="mt-2 text-[11px] text-ink/55">
                Shopkeeper uploaded a photo of the licence displayed on their shop wall — real shops, real proof.
              </div>
            </section>
          )}


          {(store.lat != null && store.lng != null) && (
            <section data-testid="shop-map" className="card-soft p-5 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-[#D35400]"/>
                <div className="font-heading font-bold text-lg">Find us on the map</div>
                <a href={`https://www.google.com/maps?q=${store.lat},${store.lng}`} target="_blank" rel="noreferrer" className="ml-auto text-xs font-semibold text-[#D35400] hover:underline">Open in Google Maps →</a>
              </div>
              <div className="rounded-lg overflow-hidden border border-border">
                <iframe
                  title={`${store.name} location`}
                  src={`https://www.google.com/maps?q=${store.lat},${store.lng}&z=16&output=embed`}
                  width="100%" height="300" style={{ border: 0 }}
                  loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen
                />
              </div>
            </section>
          )}

          <h2 className="font-heading font-bold text-2xl text-ink">Products</h2>
          <p className="text-sm text-ink/70 mt-1">{products.length} items available · Click any item to see similar from other stores</p>
          <div data-testid="products-grid" className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
            {products.map(p => (
              <div
                key={p.id}
                onClick={() => setSelectedProductId(p.id)}
                className={`cursor-pointer rounded-xl transition ${selectedProductId === p.id ? 'ring-2 ring-[#E67E22]' : ''}`}
              >
                <ProductCard product={p} store={store}/>
              </div>
            ))}
            {products.length === 0 && <div className="text-ink/60">No products listed yet.</div>}
          </div>

          {/* Amazon-style similar items / quick comparison */}
          {selectedProductId && (
            <SimilarItems productId={selectedProductId} onClose={() => setSelectedProductId(null)}/>
          )}
          {!selectedProductId && products.length > 0 && (
            <div className="mt-7 rounded-xl border border-dashed border-border bg-[#FBF8F1] p-5 text-center text-sm text-ink/55">
              Tap any product above to see <b className="text-ink">similar items from other stores</b> with a quick price comparison.
            </div>
          )}

          <ReviewSection storeId={store.id}/>
          <ReputationPanel kind="store" targetId={store.id} title="Verified customer reviews (with photos)"/>
        </div>

        <aside className="lg:sticky lg:top-24 self-start card-soft p-5 h-fit">
          <div className="flex items-center gap-2 text-ink">
            <ShoppingBag className="w-5 h-5 text-[#E67E22]"/>
            <div className="font-heading font-bold text-lg">Your cart</div>
          </div>
          {cart.store && cart.store.id !== store.id && (
            <div className="mt-3 text-xs rounded-lg bg-[#FFF3E0] text-[#9A4500] p-3 border border-[#FFCC80]">
              Adding will replace items from <b>{cart.store.name}</b>.
            </div>
          )}
          <div className="mt-3 text-sm text-ink/70">{cart.items.length === 0 ? 'Cart is empty.' : `${cart.totals.count} items · ₹${cart.totals.subtotal.toFixed(2)}`}</div>
          <Link to="/cart" data-testid="go-to-cart" className={`mt-4 btn-primary w-full ${cart.items.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}>Go to cart <ArrowRight className="w-4 h-4"/></Link>
        </aside>
      </div>
    </div>
  );
}

function Info({ Icon, label, value, tone }) {
  const toneCls = tone === 'success' ? 'text-[#1B5E20]' : 'text-ink';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/50 inline-flex items-center gap-1"><Icon className="w-3 h-3"/>{label}</div>
      <div className={`mt-1 text-sm leading-snug ${toneCls}`}>{value}</div>
    </div>
  );
}
